import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api';
import { useAudit, useMe, useUsers } from '../../hooks';
import { formatImported } from '../../shared/format';
import { PERSONAS, PERSONA_LABEL, isManager, type PortalPersona } from '../../shared/persona';

const ACTION_LABEL: Record<string, string> = {
  login: '登录',
  login_failed: '登录失败',
  logout: '退出',
  password_changed: '修改密码',
  user_created: '创建账号',
  user_registered: '自助注册',
  user_updated: '更新账号',
  dataset_archived: '归档数据集',
  release_published: '发布上线',
};

function randomPassword(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export default function Users() {
  const me = useMe();
  const isAdmin = isManager(me.data?.persona ?? 'visitor');
  const users = useUsers(isAdmin);
  const audit = useAudit(isAdmin);
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    username: '',
    display_name: '',
    persona: 'current_student' as PortalPersona,
    password: randomPassword(),
    unit: '',
    phone: '',
    student_no: '',
    is_read_only: false,
  });
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['users'] }),
      queryClient.invalidateQueries({ queryKey: ['audit'] }),
    ]);
  };

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await api('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ ...form, real_name: form.display_name }),
      });
      setNotice({
        kind: 'ok',
        text: `已创建 ${form.username}，初始密码：${form.password}（首次登录需改密）`,
      });
      setForm({
        username: '',
        display_name: '',
        persona: 'current_student',
        password: randomPassword(),
        unit: '',
        phone: '',
        student_no: '',
        is_read_only: false,
      });
      await refresh();
    } catch (err) {
      setNotice({
        kind: 'err',
        text:
          err instanceof ApiError && err.code === 'username_taken'
            ? '用户名已存在'
            : err instanceof ApiError && err.code === 'phone_taken'
              ? '手机号已被使用'
              : err instanceof ApiError && err.code === 'invalid_phone'
                ? '手机号格式不正确'
                : '创建失败，请检查用户名和密码（至少 4 位）',
      });
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id: number, body: Record<string, unknown>, success?: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await api(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (success) setNotice({ kind: 'ok', text: success });
      await refresh();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setNotice({
        kind: 'err',
        text:
          code === 'last_admin'
            ? '不能移除最后一个管理员/组内教师'
            : code === 'cannot_deactivate_self'
              ? '不能停用自己的账号'
              : '操作失败',
      });
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="empty">
        <b>仅管理员可见</b>
        当前账号没有用户管理权限。
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Admin</div>
          <h1>用户与审计</h1>
        </div>
        <span className="badge">{users.data?.length ?? 0} 个账号</span>
      </div>

      {notice ? <div className={`notice ${notice.kind}`}>{notice.text}</div> : null}

      <div className="admin-grid">
        <section className="panel" style={{ padding: 18 }}>
          <div className="section-head">
            <h2>账号</h2>
            <span className="muted" style={{ fontSize: 11 }}>
              自助注册账号默认访客，可在此调整角色
            </span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ minWidth: 150 }}>账号</th>
                  <th style={{ minWidth: 90 }}>姓名</th>
                  <th style={{ minWidth: 190 }}>单位 / 手机</th>
                  <th style={{ minWidth: 100 }}>角色</th>
                  <th style={{ minWidth: 130 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {(users.data ?? []).map((user) => {
                  const isSelf = user.id === me.data?.id;
                  return (
                    <tr key={user.id}>
                      <td>
                        <b>{user.username}</b>
                        {user.is_read_only ? (
                          <span className="badge" style={{ marginLeft: 6 }}>
                            只读
                          </span>
                        ) : null}
                        <span
                          className={`badge ${user.is_active ? 'ok' : 'danger'}`}
                          style={{ marginLeft: 6 }}
                        >
                          {user.is_active ? '启用' : '停用'}
                        </span>
                        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                          {user.last_login_at
                            ? `最近登录 ${formatImported(user.last_login_at)}`
                            : '从未登录'}
                        </div>
                      </td>
                      <td>
                        <div>{user.real_name ?? user.display_name}</div>
                        {user.real_name && user.real_name !== user.display_name ? (
                          <div className="muted" style={{ fontSize: 11 }}>
                            {user.display_name}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div>{user.unit ?? '—'}</div>
                        <div className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                          {user.phone ?? '—'}
                          {user.student_no ? ` · 学号 ${user.student_no}` : ''}
                        </div>
                      </td>
                      <td>
                        <select
                          className="input"
                          style={{ height: 32, padding: '0 8px', width: 110 }}
                          value={user.persona}
                          disabled={busy || isSelf}
                          onChange={(event) =>
                            patch(user.id, { persona: event.target.value }, '角色已更新')
                          }
                        >
                          {PERSONAS.map((value) => (
                            <option key={value} value={value}>
                              {PERSONA_LABEL[value]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={busy || isSelf}
                            onClick={() =>
                              patch(
                                user.id,
                                { is_active: !user.is_active },
                                user.is_active ? '账号已停用' : '账号已启用',
                              )
                            }
                          >
                            {user.is_active ? '停用' : '启用'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={busy || isSelf}
                            onClick={() => {
                              const password = randomPassword();
                              patch(
                                user.id,
                                { password },
                                `${user.username} 的新密码：${password}（首次登录需改密）`,
                              );
                            }}
                          >
                            重置密码
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="side-card">
          <h3>新建账号</h3>
          <p className="caption">
            新账号首次登录会被要求设置自己的密码。测试期允许 4 位，对外开放前收紧。
          </p>
          <form onSubmit={create}>
            <div className="field">
              <label htmlFor="new-username">用户名</label>
              <input
                id="new-username"
                className="input"
                value={form.username}
                onChange={(event) => setForm({ ...form, username: event.target.value })}
                placeholder="字母、数字、. _ -"
                required
              />
            </div>
            <div className="field">
              <label htmlFor="new-display">姓名</label>
              <input
                id="new-display"
                className="input"
                value={form.display_name}
                onChange={(event) => setForm({ ...form, display_name: event.target.value })}
                placeholder="显示名称"
                required
              />
            </div>
            <div className="field">
              <label htmlFor="new-unit">所属单位</label>
              <input
                id="new-unit"
                className="input"
                value={form.unit}
                onChange={(event) => setForm({ ...form, unit: event.target.value })}
                placeholder="选填"
              />
            </div>
            <div className="field">
              <label htmlFor="new-phone">手机号</label>
              <input
                id="new-phone"
                className="input"
                type="tel"
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
                placeholder="选填，用于联系"
              />
            </div>
            <div className="field">
              <label htmlFor="new-student-no">学号</label>
              <input
                id="new-student-no"
                className="input"
                value={form.student_no}
                onChange={(event) => setForm({ ...form, student_no: event.target.value })}
                placeholder="选填"
              />
            </div>
            <div className="field">
              <label htmlFor="new-role">角色</label>
              <select
                id="new-role"
                className="input"
                value={form.persona}
                onChange={(event) =>
                  setForm({ ...form, persona: event.target.value as PortalPersona })
                }
              >
                {PERSONAS.map((value) => (
                  <option key={value} value={value}>
                    {PERSONA_LABEL[value]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="new-password">初始密码</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  id="new-password"
                  className="input"
                  style={{ flex: 1, minWidth: 0 }}
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                  minLength={4}
                  required
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setForm({ ...form, password: randomPassword() })}
                >
                  生成
                </button>
              </div>
            </div>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.is_read_only}
                onChange={(event) => setForm({ ...form, is_read_only: event.target.checked })}
              />
              只读（预留）
            </label>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? '处理中…' : '创建账号'}
            </button>
          </form>
        </aside>
      </div>

      <section className="panel" style={{ padding: 18, marginTop: 18 }}>
        <div className="section-head">
          <h2>最近审计</h2>
          <span className="muted" style={{ fontSize: 11 }}>
            最近 200 条，按时间倒序
          </span>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>时间</th>
                <th>用户</th>
                <th>动作</th>
                <th>对象</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {(audit.data ?? []).slice(0, 30).map((entry) => (
                <tr key={entry.id}>
                  <td className="muted">{formatImported(entry.created_at)}</td>
                  <td>{entry.username ?? '—'}</td>
                  <td>{ACTION_LABEL[entry.action] ?? entry.action}</td>
                  <td>{entry.target ?? '—'}</td>
                  <td className="mono">{entry.ip ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}